import React from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  BadgeCheck,
  CalendarDays,
  CircleHelp,
  Euro,
  FileText,
  Gauge,
  Home,
  Pencil,
  Plus,
  Save,
  UploadCloud,
  X,
  Download,
  LoaderCircle,
  Trash2,
  ChevronDown,
} from "lucide-react";
import { createPortal } from "react-dom";

import "./RenovationProjectPreAssessmentStep.css";
import { useAuth0 } from "@auth0/auth0-react";
import { useToast } from "../../../components/feedback/ToastProvider";

const MODAL_CLOSE_MS = 180;

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";
const STEP_DRAFT_KEY = "PRE_ASSESSMENT";

/* =========================================================
   PRE-ASSESSMENT TECHNICAL OPTIONS
   ========================================================= */

const ACCREDITATION_OPTIONS = [
  "ARCHITECT",
  "TECHNICAL_ARCHITECT",
  "ENGINEER",
  "TECHNICAL_ENGINEER",
];

const SECTOR_OPTIONS = ["BUILDING_REHABILITATION", "RENEWABLE_ENERGIES"];

const RATING_OPTIONS = ["A_PLUS", "A", "B", "C", "D", "E", "F", "G"];

/* =========================================================
   ENERGY-EFFICIENCY MEASURES
   ========================================================= */

const MEASURE_OPTIONS = [
  "LUMINAIRES",
  "COVERS",
  "SOILS",
  "SUN_PROTECTION",
  "HVAC_BOILER",
  "HVAC_PIPING",
  "HVAC_CONTROLS",
  "PHOTOVOLTAIC_SYSTEMS",
  "SOLAR_THERMAL_SYSTEMS",
  "NETWORK_SMART_METERS",
  "ENVELOPE",
  "GLAZING",
  "HVAC",
  "HVAC_REFRIGERATION",
  "HVAC_VENTILATION",
  "HVAC_DISTRICT_HEATING",
  "BIOMASS_BOILERS",
  "COMPUTER_SYSTEMS",
  "OTHER",
];

const EMPTY_TECHNICAL_RESULTS = {
  certifierName: "",
  professionalAccreditation: "",
  certificateDate: "",
  sector: "",
  energyRating: "",
  emissionRating: "",
  primaryNonRenewableEnergyConsumption: "",
  co2Emissions: "",
};

const EMPTY_PROPOSAL = {
  reportCompletedOn: "",
  leadName: "",
  actionDescription: "",
  energySavingsPercentage: "",
  totalEstimatedBudget: "",
  greatestSavingRenewable: "",
  selectedMeasures: [],
  otherMeasures: "",
};

function findStep(steps = []) {
  return (
    steps.find((step) => step.step_code === "PRE_ASSESSMENT") || {
      step_code: "PRE_ASSESSMENT",
      step_order: 2,
      status: "NOT_STARTED",
      editable_by: "OSS",
    }
  );
}

function statusClass(status) {
  return String(status || "PLANNING")
    .toLowerCase()
    .replaceAll("_", "-");
}

function formatSummaryDate(value) {
  if (!value) {
    return "—";
  }

  const date = new Date(`${value}T00:00:00`);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function formatPercentage(value) {
  if (value === "" || value === null || value === undefined) {
    return "—";
  }

  return `${value} %`;
}

function formatBudget(value) {
  if (value === "" || value === null || value === undefined) {
    return "—";
  }

  const numberValue = Number(value);

  if (Number.isNaN(numberValue)) {
    return `${value} €`;
  }

  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(numberValue);
}

function renewableFromApi(value) {
  if (value === true) {
    return "YES";
  }

  if (value === false) {
    return "NO";
  }

  return "";
}

function renewableToApi(value) {
  if (value === "YES") {
    return true;
  }

  if (value === "NO") {
    return false;
  }

  return null;
}

function proposalHasData(proposal) {
  if (!proposal) {
    return false;
  }

  return Boolean(
    proposal.reportCompletedOn ||
    proposal.leadName ||
    proposal.actionDescription ||
    proposal.energySavingsPercentage ||
    proposal.totalEstimatedBudget ||
    proposal.greatestSavingRenewable ||
    proposal.otherMeasures ||
    (proposal.selectedMeasures || []).length > 0,
  );
}

export default function RenovationProjectPreAssessmentStep() {
  const { t } = useTranslation("oss-project-pre-assessment");

  const navigate = useNavigate();

  const {
    project,
    steps,
    basePath,
    viewerRole,

    registerStepSaveHandler,
    setStepDirty,
    isStepDirty,
    saveCurrentStep,

    getStepDraft,
    setStepDraft,
    clearStepDraft,
  } = useOutletContext();

  const step = findStep(steps);

  const projectStatus = project?.status || "PLANNING";

  const canEdit = viewerRole === "OSS";

  const [documentFile, setDocumentFile] = React.useState(null);

  const [documentInfo, setDocumentInfo] = React.useState(null);

  const [isDownloadingDocument, setIsDownloadingDocument] =
    React.useState(false);

  const [preAssessmentLoading, setPreAssessmentLoading] = React.useState(true);

  const [preAssessmentError, setPreAssessmentError] = React.useState("");

  const [technicalResults, setTechnicalResults] = React.useState(
    EMPTY_TECHNICAL_RESULTS,
  );

  const [savedProposal, setSavedProposal] = React.useState(null);

  const [proposalDraft, setProposalDraft] = React.useState(EMPTY_PROPOSAL);

  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const [isModalClosing, setIsModalClosing] = React.useState(false);

  const closeTimerRef = React.useRef(null);

  const localDirtyRef = React.useRef(false);

  const draftSnapshotRef = React.useRef(null);

  const fileInputRef = React.useRef(null);

  const hasProposal = Boolean(savedProposal);

  const { getAccessTokenSilently } = useAuth0();

  const { showToast } = useToast();

  const [isDeletingDocument, setIsDeletingDocument] = React.useState(false);

  const [deleteDocumentModalOpen, setDeleteDocumentModalOpen] =
    React.useState(false);

  /* =========================================================
     CLEANUP MODAL TIMER
     ========================================================= */

  React.useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  /* =========================================================
     ESCAPE TO CLOSE
     ========================================================= */

  React.useEffect(() => {
    if (!isModalOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeModal();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isModalOpen, isModalClosing]);

  /* =========================================================
     TECHNICAL RESULTS
     ========================================================= */

  const updateTechnicalResult = (field, value) => {
    if (!canEdit) {
      return;
    }

    setTechnicalResults((current) => ({
      ...current,
      [field]: value,
    }));

    localDirtyRef.current = true;
    setStepDirty(true);
  };

  /* =========================================================
     MODAL OPEN / CLOSE
     ========================================================= */

  const startModalOpen = () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);

      closeTimerRef.current = null;
    }

    setIsModalClosing(false);
    setIsModalOpen(true);
  };

  const openNewProposal = () => {
    if (!canEdit) {
      return;
    }

    setProposalDraft({
      ...EMPTY_PROPOSAL,
      selectedMeasures: [],
    });

    startModalOpen();
  };

  const openProposalDetails = () => {
    if (!savedProposal) {
      return;
    }

    setProposalDraft({
      ...savedProposal,
      selectedMeasures: [...(savedProposal.selectedMeasures || [])],
    });

    startModalOpen();
  };

  const closeModal = () => {
    if (!isModalOpen || isModalClosing) {
      return;
    }

    setIsModalClosing(true);

    closeTimerRef.current = window.setTimeout(() => {
      setIsModalOpen(false);

      setIsModalClosing(false);

      closeTimerRef.current = null;
    }, MODAL_CLOSE_MS);
  };

  /* =========================================================
     PROPOSAL DRAFT
     ========================================================= */

  const updateDraft = (field, value) => {
    if (!canEdit) {
      return;
    }

    setProposalDraft((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const toggleMeasure = (measureCode) => {
    if (!canEdit) {
      return;
    }

    setProposalDraft((current) => {
      const selected = current.selectedMeasures || [];

      const exists = selected.includes(measureCode);

      return {
        ...current,

        selectedMeasures: exists
          ? selected.filter((item) => item !== measureCode)
          : [...selected, measureCode],
      };
    });
  };

  /* =========================================================
   DOWNLOAD PRE-ASSESSMENT DOCUMENT
   ========================================================= */

  const downloadPreAssessmentDocument = async () => {
    if (!documentInfo?.id || !project?.id || isDownloadingDocument) {
      return;
    }

    setIsDownloadingDocument(true);
    setPreAssessmentError("");

    try {
      const token = await getAccessTokenSilently();

      const response = await fetch(
        `${API_BASE}/api/projects/${project.id}/documents/${documentInfo.id}/file/?disposition=attachment`,
        {
          method: "GET",

          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));

        throw new Error(
          data?.detail ||
            t(
              "errors.downloadFailed",
              "Failed to download the pre-assessment document.",
            ),
        );
      }

      const blob = await response.blob();

      const objectUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = objectUrl;

      link.download =
        documentInfo.filename ||
        documentInfo.original_filename ||
        "pre-assessment.pdf";

      document.body.appendChild(link);

      link.click();

      link.remove();

      window.URL.revokeObjectURL(objectUrl);
    } catch (requestError) {
      setPreAssessmentError(
        requestError?.message ||
          t(
            "errors.downloadFailed",
            "Failed to download the pre-assessment document.",
          ),
      );
    } finally {
      setIsDownloadingDocument(false);
    }
  };

  const deletePreAssessmentDocument = async () => {
    if (!project?.id || !documentInfo?.id || isDeletingDocument) {
      return;
    }

    setIsDeletingDocument(true);

    try {
      const token = await getAccessTokenSilently();

      const response = await fetch(
        `${API_BASE}/api/projects/${project.id}/pre-assessment/document/`,
        {
          method: "DELETE",

          headers: {
            Authorization: `Bearer ${token}`,

            Accept: "application/json",
          },
        },
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data?.detail ||
            t(
              "errors.deleteDocumentFailed",
              "Failed to delete the pre-assessment document.",
            ),
        );
      }

      setDocumentInfo(null);
      setDocumentFile(null);

      setDeleteDocumentModalOpen(false);

      showToast({
        type: "success",

        title: t("toasts.documentDeletedTitle", "Document removed"),

        message: t(
          "toasts.documentDeletedMessage",
          "The pre-assessment document was removed successfully.",
        ),
      });

      await loadPreAssessment();
    } catch (requestError) {
      showToast({
        type: "error",

        title: t(
          "toasts.documentDeleteErrorTitle",
          "Could not remove document",
        ),

        message:
          requestError?.message ||
          t(
            "errors.deleteDocumentFailed",
            "Failed to delete the pre-assessment document.",
          ),
      });
    } finally {
      setIsDeletingDocument(false);
    }
  };

  /* =========================================================
     DOCUMENT
     ========================================================= */

  const handleDrop = (event) => {
    event.preventDefault();

    if (!canEdit) {
      return;
    }

    const file = event.dataTransfer.files?.[0];

    if (file) {
      setDocumentFile(file);

      localDirtyRef.current = true;
      setStepDirty(true);
    }
  };

  const handleFileChange = (event) => {
    if (!canEdit) {
      return;
    }

    const file = event.target.files?.[0];

    if (file) {
      setDocumentFile(file);

      localDirtyRef.current = true;
      setStepDirty(true);
    }
  };

  /* =========================================================
     SAVE PROPOSAL
     ========================================================= */

  const saveProposal = () => {
    if (!canEdit) {
      return;
    }

    const nextProposal = {
      ...proposalDraft,

      selectedMeasures: [...(proposalDraft.selectedMeasures || [])],
    };

    setSavedProposal(nextProposal);

    localDirtyRef.current = true;
    setStepDirty(true);

    closeModal();
  };

  const goNext = async () => {
    /*
     * The yellow button really means "Save and continue" for an editable
     * OSS step. If there are unsaved changes, persist them first. A failed
     * save keeps the user on Step 2.
     */
    if (canEdit && isStepDirty && saveCurrentStep) {
      const saved = await saveCurrentStep();

      if (!saved) {
        return;
      }
    }

    navigate(`${basePath}/${project.id}/renovation-plan`);
  };

  /* =========================================================
   LOAD PRE-ASSESSMENT FROM BACKEND
   ========================================================= */

  const loadPreAssessment = React.useCallback(async () => {
    if (!project?.id) {
      return;
    }

    setPreAssessmentLoading(true);
    setPreAssessmentError("");

    try {
      const token = await getAccessTokenSilently();

      const response = await fetch(
        `${API_BASE}/api/projects/${project.id}/pre-assessment/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,

            Accept: "application/json",
          },
        },
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data?.detail || "Failed to load pre-assessment.");
      }

      /* -----------------------------------------------
         Technical results
         ----------------------------------------------- */

      setTechnicalResults({
        certifierName: data.certifier_name || "",

        professionalAccreditation: data.professional_accreditation || "",

        certificateDate: data.certificate_date || "",

        sector: data.sector || "",

        energyRating: data.energy_rating || "",

        emissionRating: data.emission_rating || "",

        primaryNonRenewableEnergyConsumption:
          data.primary_non_renewable_energy_consumption ?? "",

        co2Emissions: data.co2_emissions ?? "",
      });

      /* -----------------------------------------------
         Proposal
         ----------------------------------------------- */

      const loadedProposal = {
        reportCompletedOn: data.preliminary_report_completed_on || "",

        leadName: data.lead_name || "",

        actionDescription: data.action_description || "",

        energySavingsPercentage: data.energy_savings_percentage ?? "",

        totalEstimatedBudget:
          data.total_estimated_budget_without_subsidies ?? "",

        greatestSavingRenewable: renewableFromApi(
          data.greatest_saving_from_renewable_energy,
        ),

        selectedMeasures: Array.isArray(data.selected_action_measures)
          ? data.selected_action_measures
          : [],

        otherMeasures: data.other_energy_improvement_measures || "",
      };

      setSavedProposal(proposalHasData(loadedProposal) ? loadedProposal : null);

      setDocumentInfo(data.document || null);

      setDocumentFile(null);

      localDirtyRef.current = false;
      setStepDirty(false);
    } catch (requestError) {
      setPreAssessmentError(
        requestError?.message || "Failed to load pre-assessment.",
      );
    } finally {
      setPreAssessmentLoading(false);
    }
  }, [getAccessTokenSilently, project?.id, setStepDirty]);

  /* =========================================================
     KEEP LATEST LOCAL DRAFT SNAPSHOT
     ========================================================= */

  React.useEffect(() => {
    draftSnapshotRef.current = {
      technicalResults,
      savedProposal,
      documentFile,
      documentInfo,
    };
  }, [technicalResults, savedProposal, documentFile, documentInfo]);

  /* =========================================================
     PRESERVE UNSAVED VALUES WHEN LEAVING STEP 2
     ========================================================= */

  React.useEffect(() => {
    return () => {
      if (localDirtyRef.current && draftSnapshotRef.current) {
        setStepDraft(STEP_DRAFT_KEY, draftSnapshotRef.current);
      }
    };
  }, [setStepDraft]);

  /* =========================================================
     INITIALISE STEP 2

     1. Prefer an unsaved in-memory draft when one exists.
     2. Otherwise load the canonical saved values from the backend.

     This is the missing piece in the previous component: the GET callback
     existed, but it was never called when Step 2 mounted.
     ========================================================= */

  React.useEffect(() => {
    const draft = getStepDraft?.(STEP_DRAFT_KEY);

    if (draft) {
      setTechnicalResults(
        draft.technicalResults || {
          ...EMPTY_TECHNICAL_RESULTS,
        },
      );

      setSavedProposal(draft.savedProposal || null);

      setDocumentFile(draft.documentFile || null);

      setDocumentInfo(draft.documentInfo || null);

      localDirtyRef.current = true;

      setPreAssessmentError("");
      setPreAssessmentLoading(false);

      setStepDirty(true);

      return;
    }

    loadPreAssessment();
  }, [getStepDraft, loadPreAssessment, setStepDirty]);

  /* =========================================================
   SAVE COMPLETE STEP 2

   Saves:
   - all technical-result fields
   - all proposed-improvement fields
   - new/replacement PDF if selected
   ========================================================= */

  const savePreAssessment = React.useCallback(async () => {
    if (!canEdit || !project?.id) {
      return;
    }

    const token = await getAccessTokenSilently();

    const proposal = savedProposal || EMPTY_PROPOSAL;

    const payload = {
      /* ---------------------------------------------
         Technical results
         --------------------------------------------- */

      certifier_name: technicalResults.certifierName || "",

      professional_accreditation:
        technicalResults.professionalAccreditation || "",

      certificate_date: technicalResults.certificateDate || null,

      sector: technicalResults.sector || "",

      energy_rating: technicalResults.energyRating || "",

      emission_rating: technicalResults.emissionRating || "",

      primary_non_renewable_energy_consumption:
        technicalResults.primaryNonRenewableEnergyConsumption === ""
          ? null
          : technicalResults.primaryNonRenewableEnergyConsumption,

      co2_emissions:
        technicalResults.co2Emissions === ""
          ? null
          : technicalResults.co2Emissions,

      /* ---------------------------------------------
         Proposed improvements
         --------------------------------------------- */

      preliminary_report_completed_on: proposal.reportCompletedOn || null,

      lead_name: proposal.leadName || "",

      action_description: proposal.actionDescription || "",

      energy_savings_percentage:
        proposal.energySavingsPercentage === ""
          ? null
          : proposal.energySavingsPercentage,

      total_estimated_budget_without_subsidies:
        proposal.totalEstimatedBudget === ""
          ? null
          : proposal.totalEstimatedBudget,

      greatest_saving_from_renewable_energy: renewableToApi(
        proposal.greatestSavingRenewable,
      ),

      selected_action_measures: proposal.selectedMeasures || [],

      other_energy_improvement_measures: proposal.otherMeasures || "",
    };

    const patchResponse = await fetch(
      `${API_BASE}/api/projects/${project.id}/pre-assessment/update/`,
      {
        method: "PATCH",

        headers: {
          Authorization: `Bearer ${token}`,

          Accept: "application/json",

          "Content-Type": "application/json",
        },

        body: JSON.stringify(payload),
      },
    );

    const patchData = await patchResponse.json().catch(() => ({}));

    if (!patchResponse.ok) {
      throw new Error(
        patchData?.detail ||
          Object.values(patchData || {})
            .flat()
            .join(" ") ||
          "Failed to save pre-assessment.",
      );
    }

    if (documentFile instanceof File) {
      const formData = new FormData();

      formData.append("file", documentFile);

      const uploadResponse = await fetch(
        `${API_BASE}/api/projects/${project.id}/pre-assessment/document/`,
        {
          method: "POST",

          headers: {
            Authorization: `Bearer ${token}`,
          },

          body: formData,
        },
      );

      const uploadData = await uploadResponse.json().catch(() => ({}));

      if (!uploadResponse.ok) {
        throw new Error(
          uploadData?.detail ||
            uploadData?.file ||
            "Failed to upload pre-assessment document.",
        );
      }

      setDocumentInfo(uploadData.document || null);

      setDocumentFile(null);
    }

    await loadPreAssessment();

    /*
     * Backend is now the canonical source again. Remove any in-memory draft
     * so returning to Step 2 loads the freshly persisted record.
     */
    clearStepDraft?.(STEP_DRAFT_KEY);

    localDirtyRef.current = false;
    setStepDirty(false);
  }, [
    canEdit,
    clearStepDraft,
    documentFile,
    getAccessTokenSilently,
    loadPreAssessment,
    project?.id,
    savedProposal,
    setStepDirty,
    technicalResults,
  ]);

  React.useEffect(() => {
    if (!canEdit || !registerStepSaveHandler) {
      return undefined;
    }

    return registerStepSaveHandler(savePreAssessment);
  }, [canEdit, registerStepSaveHandler, savePreAssessment]);

  /* =========================================================
   STEP LOADING
   ========================================================= */

  if (preAssessmentLoading) {
    return (
      <div className="renovation-step-loader">
        <LoaderCircle size={30} className="renovation-step-loader__spinner" />

        <span>{t("loading.preAssessment", "Loading pre-assessment...")}</span>
      </div>
    );
  }

  if (preAssessmentError) {
    return (
      <div className="renovation-step-load-error">
        <FileText size={26} />

        <strong>
          {t("errors.loadTitle", "Pre-assessment could not be loaded")}
        </strong>

        <span>{preAssessmentError}</span>

        <button type="button" onClick={loadPreAssessment}>
          {t("buttons.tryAgain", "Try again")}
        </button>
      </div>
    );
  }

  return (
    <article className="renovation-pre-step">
      {/* ===================================================
          HEADER
          =================================================== */}

      <header className="renovation-pre-step__header">
        <div>
          <span className="renovation-pre-step__eyebrow">
            <Home size={15} />

            {t("preAssessment.eyebrow", "Renovation project")}
          </span>

          <h1>{t("preAssessment.title", "Pre-assessment")}</h1>

          <p>
            {t(
              "preAssessment.subtitle",
              "Capture the technical assessment of the building and the key priorities for renovation.",
            )}
          </p>
        </div>

        <div className="renovation-pre-step__status">
          <span className="renovation-pre-step__stage-count">
            {t("preAssessment.stage", "Stage {{current}} of {{total}}", {
              current: step.step_order || 2,
              total: 8,
            })}
          </span>

          <span
            className={
              `renovation-pre-step__status-pill ` +
              `renovation-pre-step__status-pill--${statusClass(projectStatus)}`
            }
          >
            {t(`projectStatuses.${projectStatus}`, projectStatus)}
          </span>
        </div>
      </header>

      {/* ===================================================
          PRE-ASSESSMENT CARD
          =================================================== */}

      <section className="renovation-pre-section">
        <div className="renovation-pre-section__title">
          <FileText size={18} />

          <h2>{t("preAssessment.cardTitle", "Pre-assessment")}</h2>
        </div>

        {/* -------------------------------------------------
            REPORT DOCUMENT
            ------------------------------------------------- */}

        <div className="renovation-pre-upload-block">
          <span className="renovation-pre-field-label">
            {t("fields.uploadDocument", "Pre-assessment document")}
          </span>

          {/* =====================================================
      EXISTING SAVED DOCUMENT
      ===================================================== */}

          {documentInfo ? (
            <div className="renovation-pre-saved-document">
              <div className="renovation-pre-saved-document__icon">
                <FileText size={21} />
              </div>

              <div className="renovation-pre-saved-document__content">
                <span>{t("upload.currentDocument", "Current document")}</span>

                <strong>
                  {documentInfo.filename ||
                    documentInfo.original_filename ||
                    "pre-assessment.pdf"}
                </strong>
              </div>

              <div className="renovation-pre-saved-document__actions">
                <button
                  type="button"
                  className="renovation-pre-document-action"
                  onClick={downloadPreAssessmentDocument}
                  disabled={isDownloadingDocument}
                >
                  {isDownloadingDocument ? (
                    <LoaderCircle
                      size={15}
                      className="renovation-step-loader__spinner"
                    />
                  ) : (
                    <Download size={15} />
                  )}

                  {isDownloadingDocument
                    ? t("buttons.downloading", "Downloading...")
                    : t("buttons.download", "Download")}
                </button>

                {canEdit ? (
                  <>
                    <button
                      type="button"
                      className="renovation-pre-document-action"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <UploadCloud size={15} />

                      {t("buttons.replaceDocument", "Replace")}
                    </button>

                    <button
                      type="button"
                      className="renovation-pre-document-action renovation-pre-document-action--danger"
                      onClick={() => setDeleteDocumentModalOpen(true)}
                      disabled={isDeletingDocument}
                    >
                      <Trash2 size={15} />

                      {t("buttons.deleteDocument", "Delete")}
                    </button>
                  </>
                ) : null}
              </div>
            </div>
          ) : null}

          {/* =====================================================
      NEW FILE / REPLACEMENT SELECTED
      ===================================================== */}

          {canEdit && documentFile ? (
            <div className="renovation-pre-selected-file">
              <UploadCloud size={19} />

              <div>
                <span>
                  {documentInfo
                    ? t("upload.replacementSelected", "Replacement selected")
                    : t("upload.fileSelected", "File selected")}
                </span>

                <strong>{documentFile.name}</strong>
              </div>

              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();

                  setDocumentFile(null);

                  localDirtyRef.current = true;

                  setStepDirty(true);
                }}
                aria-label={t(
                  "buttons.removeSelectedFile",
                  "Remove selected file",
                )}
              >
                <X size={16} />
              </button>
            </div>
          ) : null}

          {/* =====================================================
      UPLOAD AREA
      ===================================================== */}

          {canEdit && !documentInfo && !documentFile ? (
            <div
              className="renovation-pre-dropzone"
              onDragOver={(event) => event.preventDefault()}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();

                  fileInputRef.current?.click();
                }
              }}
              role="button"
              tabIndex={0}
            >
              <UploadCloud size={20} />

              <strong>
                {t("upload.dragDropPrefix", "Drag & drop a PDF here or")}
              </strong>

              <span>{t("upload.browse", "browse")}</span>
            </div>
          ) : null}

          {canEdit ? (
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,application/pdf"
              onChange={handleFileChange}
              hidden
            />
          ) : null}

          {!canEdit && !documentInfo ? (
            <div className="renovation-pre-readonly-block">
              <FileText size={19} />

              <div className="renovation-pre-readonly-block__content">
                <strong>
                  {t("upload.noDocument", "No document uploaded yet.")}
                </strong>

                <span>
                  {t(
                    "preAssessment.readOnlyDocument",
                    "This document is managed by the One-Stop Shop.",
                  )}
                </span>
              </div>
            </div>
          ) : null}
        </div>

        {/* -------------------------------------------------
            PRE-ASSESSMENT TECHNICAL RESULTS

            These are Step 2 results.
            They are NOT the Initial EPC from Step 1.
            ------------------------------------------------- */}

        <div className="renovation-pre-technical">
          <div className="renovation-pre-technical__header">
            <div>
              <span className="renovation-pre-technical__icon">
                <Gauge size={17} />
              </span>

              <div>
                <h3>
                  {t("technical.title", "Pre-assessment technical results")}
                </h3>

                <p>
                  {t(
                    "technical.subtitle",
                    "Record the energy-performance parameters identified in the pre-assessment. These values will later support comparison with the completed renovation.",
                  )}
                </p>
              </div>
            </div>

            {!canEdit && (
              <span className="renovation-pre-technical__readonly">
                {t("technical.readOnly", "Read only")}
              </span>
            )}
          </div>

          <div className="renovation-pre-technical-grid">
            <label>
              <span>{t("fields.certifierName", "Certifier")}</span>

              <input
                type="text"
                value={technicalResults.certifierName}
                disabled={!canEdit}
                onChange={(event) =>
                  updateTechnicalResult("certifierName", event.target.value)
                }
                placeholder={t(
                  "placeholders.certifierName",
                  "Enter certifier name",
                )}
              />
            </label>

            <label>
              <span>
                {t(
                  "fields.professionalAccreditation",
                  "Professional accreditation",
                )}
              </span>

              <div className="renovation-pre-select">
                <select
                  value={technicalResults.professionalAccreditation}
                  disabled={!canEdit}
                  onChange={(event) =>
                    updateTechnicalResult(
                      "professionalAccreditation",
                      event.target.value,
                    )
                  }
                >
                  <option value="">
                    {t("common.selectOption", "Select an option")}
                  </option>

                  {ACCREDITATION_OPTIONS.map((value) => (
                    <option key={value} value={value}>
                      {t(`accreditations.${value}`)}
                    </option>
                  ))}
                </select>

                <ChevronDown
                  size={18}
                  className="renovation-pre-select__arrow"
                  aria-hidden="true"
                />
              </div>
            </label>

            <label>
              <span>{t("fields.certificateDate", "Date of certificate")}</span>

              <input
                type="date"
                value={technicalResults.certificateDate}
                disabled={!canEdit}
                onChange={(event) =>
                  updateTechnicalResult("certificateDate", event.target.value)
                }
              />
            </label>

            <label>
              <span>{t("fields.sector", "Sector")}</span>

              <div className="renovation-pre-select">
                <select
                  value={technicalResults.sector}
                  disabled={!canEdit}
                  onChange={(event) =>
                    updateTechnicalResult("sector", event.target.value)
                  }
                >
                  <option value="">
                    {t("common.selectOption", "Select an option")}
                  </option>

                  {SECTOR_OPTIONS.map((value) => (
                    <option key={value} value={value}>
                      {t(`sectors.${value}`)}
                    </option>
                  ))}
                </select>

                <ChevronDown
                  size={18}
                  className="renovation-pre-select__arrow"
                  aria-hidden="true"
                />
              </div>
            </label>

            <label>
              <span>
                {t("fields.energyRating", "Energy rating")}

                <em className="renovation-pre-required">*</em>
              </span>

              <div className="renovation-pre-select">
                <select
                  value={technicalResults.energyRating}
                  disabled={!canEdit}
                  onChange={(event) =>
                    updateTechnicalResult("energyRating", event.target.value)
                  }
                >
                  <option value="">
                    {t("common.selectRating", "Select rating")}
                  </option>

                  {RATING_OPTIONS.map((value) => (
                    <option key={value} value={value}>
                      {t(`ratings.${value}`)}
                    </option>
                  ))}
                </select>

                <ChevronDown
                  size={18}
                  className="renovation-pre-select__arrow"
                  aria-hidden="true"
                />
              </div>
            </label>

            <label>
              <span>
                {t("fields.emissionRating", "Emission rating")}

                <em className="renovation-pre-required">*</em>
              </span>
              <div className="renovation-pre-select">
                <select
                  value={technicalResults.emissionRating}
                  disabled={!canEdit}
                  onChange={(event) =>
                    updateTechnicalResult("emissionRating", event.target.value)
                  }
                >
                  <option value="">
                    {t("common.selectRating", "Select rating")}
                  </option>

                  {RATING_OPTIONS.map((value) => (
                    <option key={value} value={value}>
                      {t(`ratings.${value}`)}
                    </option>
                  ))}
                </select>

                <ChevronDown
                  size={18}
                  className="renovation-pre-select__arrow"
                  aria-hidden="true"
                />
              </div>
            </label>

            <label>
              <span>
                {t(
                  "fields.primaryNonRenewableEnergyConsumption",
                  "Primary non-renewable energy consumption",
                )}

                <em className="renovation-pre-required">*</em>
              </span>

              <div className="renovation-pre-unit-input">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={technicalResults.primaryNonRenewableEnergyConsumption}
                  disabled={!canEdit}
                  onChange={(event) =>
                    updateTechnicalResult(
                      "primaryNonRenewableEnergyConsumption",
                      event.target.value,
                    )
                  }
                  placeholder="0.00"
                />

                <span>kWh/m²·year</span>
              </div>
            </label>

            <label>
              <span>
                {t("fields.co2Emissions", "CO₂ emissions")}

                <em className="renovation-pre-required">*</em>
              </span>

              <div className="renovation-pre-unit-input">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={technicalResults.co2Emissions}
                  disabled={!canEdit}
                  onChange={(event) =>
                    updateTechnicalResult("co2Emissions", event.target.value)
                  }
                  placeholder="0.00"
                />

                <span>kgCO₂/m²·year</span>
              </div>
            </label>
          </div>

          <p className="renovation-pre-technical__required-note">
            <span>*</span>

            {t(
              "technical.requiredNote",
              "Required before the pre-assessment can be marked as completed.",
            )}
          </p>
        </div>

        {/* -------------------------------------------------
            PROPOSED ENERGY-EFFICIENCY IMPROVEMENTS
            ------------------------------------------------- */}

        <div className="renovation-pre-proposal-block">
          <span className="renovation-pre-field-label">
            {t(
              "fields.proposedImprovements",
              "Proposed energy efficiency improvements",
            )}
          </span>

          {hasProposal ? (
            <div className="renovation-pre-summary">
              <div className="renovation-pre-summary__main">
                <div className="renovation-pre-summary__title">
                  <BadgeCheck size={18} />

                  <div>
                    <strong>
                      {savedProposal.leadName ||
                        t(
                          "proposal.savedProposalFallback",
                          "Proposed renovation action",
                        )}
                    </strong>

                    <span>
                      {t(
                        "proposal.fromReport",
                        "From preliminary report dated {{date}}",
                        {
                          date: formatSummaryDate(
                            savedProposal.reportCompletedOn,
                          ),
                        },
                      )}
                    </span>
                  </div>
                </div>

                {savedProposal.actionDescription && (
                  <p className="renovation-pre-summary__description">
                    {savedProposal.actionDescription}
                  </p>
                )}
              </div>

              <div className="renovation-pre-summary__metric">
                <span>
                  {t("proposal.energySavings", "Estimated energy savings")}
                </span>

                <strong>
                  {formatPercentage(savedProposal.energySavingsPercentage)}
                </strong>
              </div>

              <div className="renovation-pre-summary__metric">
                <span>
                  {t(
                    "proposal.estimatedBudget",
                    "Estimated budget (no subsidies)",
                  )}
                </span>

                <strong>
                  {formatBudget(savedProposal.totalEstimatedBudget)}
                </strong>
              </div>

              <div className="renovation-pre-summary__actions">
                <button
                  type="button"
                  className="renovation-pre-summary__details-btn"
                  onClick={openProposalDetails}
                >
                  {canEdit ? <Pencil size={14} /> : <FileText size={14} />}

                  {canEdit
                    ? t("buttons.viewEditDetails", "View / Edit details")
                    : t("buttons.viewDetails", "View details")}
                </button>
              </div>
            </div>
          ) : canEdit ? (
            <button
              type="button"
              className="renovation-pre-add-proposal"
              onClick={openNewProposal}
            >
              <Plus size={18} />

              <span>
                {t(
                  "buttons.addProposal",
                  "Add energy efficiency improvement proposal",
                )}
              </span>
            </button>
          ) : (
            <div className="renovation-pre-empty-proposal">
              <FileText size={18} />

              <span>
                {t(
                  "proposal.notAvailable",
                  "The One-Stop Shop has not added the proposed improvements yet.",
                )}
              </span>
            </div>
          )}
        </div>
      </section>

      {/* ===================================================
          NEXT
          =================================================== */}

      <button className="renovation-pre-next" type="button" onClick={goNext}>
        {canEdit
          ? t(
              "buttons.saveContinueRenovationPlan",
              "Save and continue to Renovation Plan",
            )
          : t("buttons.continueRenovationPlan", "Continue to Renovation Plan")}

        <ArrowRight size={17} />
      </button>

      {/* ===================================================
          PROPOSAL MODAL
          =================================================== */}

      {isModalOpen &&
        createPortal(
          <div
            className={
              isModalClosing
                ? "renovation-pre-modal-backdrop is-closing"
                : "renovation-pre-modal-backdrop"
            }
            role="presentation"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                closeModal();
              }
            }}
          >
            <section
              className="renovation-pre-modal"
              role="dialog"
              aria-modal="true"
              aria-label={t("modal.title", "Proposed action")}
              onMouseDown={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                className="renovation-pre-modal__close"
                onClick={closeModal}
                aria-label={t("buttons.close", "Close")}
              >
                <X size={18} />
              </button>

              <header className="renovation-pre-modal__header">
                <h2>{t("modal.title", "Proposed action")}</h2>

                <p>
                  {t(
                    "modal.subtitle",
                    "Add the renovation action identified in the preliminary technical report.",
                  )}
                </p>
              </header>

              <div className="renovation-pre-modal__body">
                <div className="renovation-pre-modal-grid">
                  <label>
                    {t(
                      "fields.reportCompletedOn",
                      "Date of completion of the preliminary report",
                    )}

                    <div className="renovation-pre-input-icon">
                      <CalendarDays size={16} />

                      <input
                        type="date"
                        value={proposalDraft.reportCompletedOn}
                        disabled={!canEdit}
                        onChange={(event) =>
                          updateDraft("reportCompletedOn", event.target.value)
                        }
                      />
                    </div>
                  </label>

                  <label>
                    {t("fields.leadName", "Lead name")}

                    <input
                      value={proposalDraft.leadName}
                      disabled={!canEdit}
                      onChange={(event) =>
                        updateDraft("leadName", event.target.value)
                      }
                      placeholder={t(
                        "placeholders.leadName",
                        "Enter lead name",
                      )}
                    />
                  </label>

                  <label className="renovation-pre-modal-grid__full">
                    {t(
                      "fields.actionDescription",
                      "Brief description of the action to be taken",
                    )}

                    <textarea
                      value={proposalDraft.actionDescription}
                      disabled={!canEdit}
                      onChange={(event) =>
                        updateDraft("actionDescription", event.target.value)
                      }
                      placeholder={t(
                        "placeholders.actionDescription",
                        "Describe the action to be performed",
                      )}
                    />
                  </label>

                  <label>
                    {t(
                      "fields.energySavingsPercentage",
                      "Percentage of energy savings",
                    )}

                    <div className="renovation-pre-input-suffix">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={proposalDraft.energySavingsPercentage}
                        disabled={!canEdit}
                        onChange={(event) =>
                          updateDraft(
                            "energySavingsPercentage",
                            event.target.value,
                          )
                        }
                        placeholder="0"
                      />

                      <span>%</span>
                    </div>
                  </label>

                  <label>
                    {t(
                      "fields.totalEstimatedBudget",
                      "Total estimated budget without subsidies",
                    )}

                    <div className="renovation-pre-input-suffix">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={proposalDraft.totalEstimatedBudget}
                        disabled={!canEdit}
                        onChange={(event) =>
                          updateDraft(
                            "totalEstimatedBudget",
                            event.target.value,
                          )
                        }
                        placeholder="0"
                      />

                      <Euro size={15} />
                    </div>
                  </label>
                </div>

                <div className="renovation-pre-toggle-group">
                  <span>
                    {t(
                      "fields.greatestSavingRenewable",
                      "Is the greatest energy saving from this reform due to the use of renewable energy?",
                    )}
                  </span>

                  <div>
                    <button
                      type="button"
                      disabled={!canEdit}
                      className={
                        proposalDraft.greatestSavingRenewable === "NO"
                          ? "is-selected"
                          : ""
                      }
                      onClick={() =>
                        updateDraft("greatestSavingRenewable", "NO")
                      }
                    >
                      {t("common.no", "No")}
                    </button>

                    <button
                      type="button"
                      disabled={!canEdit}
                      className={
                        proposalDraft.greatestSavingRenewable === "YES"
                          ? "is-selected"
                          : ""
                      }
                      onClick={() =>
                        updateDraft("greatestSavingRenewable", "YES")
                      }
                    >
                      {t("common.yes", "Yes")}
                    </button>
                  </div>
                </div>

                <div className="renovation-pre-measures-layout">
                  <section>
                    <span className="renovation-pre-field-label">
                      {t(
                        "fields.actionsToBeTaken",
                        "Indicate the actions to be taken",
                      )}
                    </span>

                    <div className="renovation-pre-measures">
                      {MEASURE_OPTIONS.map((measureCode) => {
                        const checkboxId = `pre-measure-${measureCode}`;

                        const checked = (
                          proposalDraft.selectedMeasures || []
                        ).includes(measureCode);

                        const measureLabel = t(`measures.${measureCode}`);

                        const measureTooltip = t(
                          `measureTooltips.${measureCode}`,
                        );

                        return (
                          <div
                            className="renovation-pre-measure-row"
                            key={measureCode}
                          >
                            <input
                              id={checkboxId}
                              className="renovation-pre-measure-input"
                              type="checkbox"
                              checked={checked}
                              disabled={!canEdit}
                              onChange={() => toggleMeasure(measureCode)}
                            />

                            <label
                              className="renovation-pre-measure-label"
                              htmlFor={checkboxId}
                            >
                              <span className="renovation-pre-measure-box" />

                              <span className="renovation-pre-measure-name">
                                {measureLabel}
                              </span>
                            </label>

                            <div className="renovation-pre-tooltip-wrap">
                              <button
                                type="button"
                                className="renovation-pre-tooltip-trigger"
                                aria-label={measureTooltip}
                              >
                                <CircleHelp size={14} />
                              </button>

                              <div
                                className="renovation-pre-tooltip"
                                role="tooltip"
                              >
                                {measureTooltip}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </section>

                  <label>
                    {t(
                      "fields.otherMeasures",
                      "Other energy improvement measures",
                    )}

                    <textarea
                      value={proposalDraft.otherMeasures}
                      disabled={!canEdit}
                      onChange={(event) =>
                        updateDraft("otherMeasures", event.target.value)
                      }
                      placeholder={t(
                        "placeholders.otherMeasures",
                        "Describe any additional energy improvement measures",
                      )}
                    />
                  </label>
                </div>
              </div>

              <footer className="renovation-pre-modal__footer">
                <button
                  type="button"
                  className="renovation-pre-modal__cancel"
                  onClick={closeModal}
                >
                  {canEdit
                    ? t("buttons.cancel", "Cancel")
                    : t("buttons.close", "Close")}
                </button>

                {canEdit && (
                  <button
                    type="button"
                    className="renovation-pre-modal__save"
                    onClick={saveProposal}
                  >
                    <Save size={16} />

                    {t("buttons.save", "Save")}
                  </button>
                )}
              </footer>
            </section>
          </div>,
          document.body,
        )}
      {deleteDocumentModalOpen &&
        createPortal(
          <div
            className="renovation-pre-modal-backdrop"
            role="presentation"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget && !isDeletingDocument) {
                setDeleteDocumentModalOpen(false);
              }
            }}
          >
            <section
              className="renovation-pre-delete-modal"
              role="dialog"
              aria-modal="true"
            >
              <div className="renovation-pre-delete-modal__icon">
                <Trash2 size={22} />
              </div>

              <h2>
                {t("deleteDocument.title", "Delete pre-assessment document?")}
              </h2>

              <p>
                {t(
                  "deleteDocument.message",
                  "The uploaded PDF will be removed from the project. The pre-assessment information entered in the form will not be deleted.",
                )}
              </p>

              {documentInfo?.filename ? (
                <div className="renovation-pre-delete-modal__file">
                  <FileText size={16} />

                  <span>{documentInfo.filename}</span>
                </div>
              ) : null}

              <div className="renovation-pre-delete-modal__actions">
                <button
                  type="button"
                  onClick={() => setDeleteDocumentModalOpen(false)}
                  disabled={isDeletingDocument}
                >
                  {t("buttons.cancel", "Cancel")}
                </button>

                <button
                  type="button"
                  className="is-danger"
                  onClick={deletePreAssessmentDocument}
                  disabled={isDeletingDocument}
                >
                  {isDeletingDocument ? (
                    <LoaderCircle
                      size={16}
                      className="renovation-step-loader__spinner"
                    />
                  ) : (
                    <Trash2 size={16} />
                  )}

                  {t("buttons.deleteDocument", "Delete document")}
                </button>
              </div>
            </section>
          </div>,
          document.body,
        )}
    </article>
  );
}
